/** @jsxImportSource @emotion/react */
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useEffect, useState, useRef } from 'react';
import { Table, Button } from 'react-bootstrap';
import { hooks as commonHooks } from '@sitebuilder/common';
import {
    lockSvg,
    // folderSvg,
    filterIconActiveSvg,
    filterIconSvg,
    chevronUpSvg,
    chevronDownSvgTwo,
    chevronRightSvg,
    gridIconGrey,
    gridIconWhite,
    listIconGrey,
    listIconWhite,
} from '../../assets/images';
import { actionCreators as pagesActionCreators } from '../../reducers/pages';
import { actionCreators as themesActionCreators } from '../../reducers/themes';
import { actionCreators as componentsActionCreators } from '../../reducers/components';
import { actionCreators as slidedecksActionCreators } from '../../reducers/slidedecks';
import { actionCreators as mediaActionCreators } from '../../reducers/media';
import { actionCreators as contentListActionCreators } from '../../reducers/contentList';
import { PageActionOption, PageNavOption, SiteNavOption } from '../Layouts/LeftSidebar/NavOptions';
import BreadCrumb from './BreadCrumb';
import AuthorEmail from './AuthorEmail';
import UserFilterInput from './UserFilterInput';
import Thumbnail from './Thumbnail';
import CheckboxSwitch from './CheckboxSwitch';

export const imageFilters: string[] = [
    '*.jpg',
    '*.jpeg',
    '*.png',
    '*.svg',
    '*.tif',
    '*.tiff',
    '*.jpe',
    '*.jxr',
    '*.bmp',
];

export const fontFilters: string[] = ['*.ttf', '*.otf', '*.woff', '*.woff2', '*.svg'];

export interface ItemsTableProps {
    tableType: string;
    redirectBaseUrl?: string;
    items: any[] | null;
    selectedItems: string[];
    onClickRow: (e: any, isSelected: boolean) => any;
    isWithinFilePicker?: boolean;
    closeMediaPicker?: () => void;
    setPath?: (path: string) => void;
    isImagePicker?: boolean;
    isFontPicker?: boolean;
    onUrlStateChange?: (state: string) => void;
    initialUrlState?: string;
    title?: string;
    subtitle?: string;
    showDeletedItems?: boolean;
    onClickShowDeletedItems?: () => void;
    options?: PageNavOption[] | SiteNavOption[] | PageActionOption[];
    selectedOption?: PageNavOption | SiteNavOption | PageActionOption;
    onClickOption?: (opt: any) => void;
}

interface PageSizes {
    [key: string]: any;
}

const ItemsTable = ({
    tableType,
    redirectBaseUrl,
    items,
    selectedItems,
    onClickRow,
    showDeletedItems,
    onClickShowDeletedItems,
    ...rest
}: ItemsTableProps) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const initialSort = { field: '', dir: '' };
    const [sort, setSort] = useState(initialSort);

    // Initialize urlState and crumbs from initialUrlState prop
    const initializeState = () => {
        const initialUrl = rest.initialUrlState || '';
        const initialCrumbs = [tableType];

        if (initialUrl) {
            // Build crumbs from the URL path
            const pathSegments = initialUrl.split('/').filter((segment) => segment);
            initialCrumbs.push(...pathSegments);
        }

        return { initialUrl, initialCrumbs };
    };

    const { initialUrl, initialCrumbs } = initializeState();
    const [urlState, setUrlState] = useState<any>(initialUrl);
    const [crumbs, setCrumbs] = useState(initialCrumbs);

    // Update internal state when initialUrlState prop changes (e.g., browser back/forward)
    useEffect(() => {
        const { initialUrl: newUrl, initialCrumbs: newCrumbs } = initializeState();
        if (newUrl !== urlState) {
            setUrlState(newUrl);
            setCrumbs(newCrumbs);
        }
    }, [rest.initialUrlState]);
    const [nameFilter, setNameFilter] = useState('');
    const [showNameFilter, setShowNameFilter] = useState(false);
    const [showAuthorFilter, setShowAuthorFilter] = useState(false);
    const [authorsInArray, setAuthorsInArray] = useState<any>([]);
    const [showGridView, setShowGridView] = useState(false);
    const authorFilterIconRef = useRef<any>(null);
    const [skip, setSkip] = useState<number>(0);
    const [take, setTake] = useState<number>(25);
    const pageSizes: PageSizes = {
        _2: 2,
        _5: 5,
        _10: 10,
        _25: 25,
        _50: 50,
        _100: 100,
        _All: 1000,
    };

    // use name value ref to prevent extra api calls when input has not been changed
    const nameValueRef = useRef(true);

    const mediaBasePath = rest?.isImagePicker ? 'images' : rest?.isFontPicker ? 'fonts' : '';


    useEffect(() => {
        if (mediaBasePath) {
            onClickFolder(mediaBasePath, true, 0, rest?.isImagePicker ? imageFilters : fontFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mediaBasePath]);

    useEffect(() => {
        if (rest.onUrlStateChange) {
            rest?.onUrlStateChange(urlState);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlState]);

    const onClickItem = (e: any) => {
        if (rest.isWithinFilePicker && rest.closeMediaPicker && rest.setPath) {
            let path = '';
            if (urlState) {
                path = `/assets/static${urlState}/${e}`;
            } else {
                path = `/assets/static/${e}`;
            }
            rest.setPath(path);
            rest.closeMediaPicker();
        } else if (tableType === 'media') {
            // For media files, open the actual file instead of navigating to a URL
            let mediaPath = '';
            if (urlState) {
                mediaPath = `/assets/static${urlState}/${e}`;
            } else {
                mediaPath = `/assets/static/${e}`;
            }
            // Open the media file in a new tab/window
            window.open(mediaPath, '_blank');
        } else {
            return history.push(
                urlState ? `${redirectBaseUrl}${urlState}/${e}` : `${redirectBaseUrl}/${e}`
            );
        }
    };

    const onClickFolder = (
        e: any,
        addCrumb: boolean,
        breadcrumbIndex?: number,
        mediaFilters?: string[]
    ) => {
        setShowNameFilter(false);
        setSkip(0);
        if (!nameValueRef?.current) {
            nameValueRef.current = true;
        }
        let _urlState = urlState;

        if (addCrumb) {
            setCrumbs([...crumbs, e]);
            _urlState = `${urlState}/${e}`;
        } else {
            const index = breadcrumbIndex || 0;
            setCrumbs(Array.isArray(crumbs) ? crumbs.slice(0, index + 1) : [tableType]);
            _urlState =
                index >= 0
                    ? urlState
                          .split('/')
                          .slice(0, index + 1)
                          .join('/')
                    : '';
        }

        switch (tableType) {
            case 'pages':
                if (_urlState.length > 0) {
                    setUrlState(_urlState);
                    dispatch(
                        pagesActionCreators.loadPages(
                            _urlState,
                            { idPattern: mediaFilters },
                            sort,
                            take,
                            0
                        )
                    );
                } else {
                    dispatch(
                        pagesActionCreators.loadPages(
                            '',
                            { idPattern: mediaFilters },
                            sort,
                            take,
                            0
                        )
                    );
                    setUrlState('');
                }
                break;
            case 'themes':
                if (_urlState.length > 0) {
                    setUrlState(_urlState);
                    dispatch(
                        themesActionCreators.listThemes(
                            _urlState,
                            { idPattern: mediaFilters },
                            sort,
                            take,
                            0
                        )
                    );
                } else {
                    dispatch(
                        themesActionCreators.listThemes(
                            '',
                            { idPattern: mediaFilters },
                            sort,
                            take,
                            0
                        )
                    );
                    setUrlState('');
                }
                break;
            case 'components':
                if (_urlState.length > 0) {
                    setUrlState(_urlState);
                    dispatch(
                        componentsActionCreators.listComponents(
                            'public',
                            _urlState,
                            { idPattern: mediaFilters },
                            sort,
                            take,
                            0
                        )
                    );
                } else {
                    dispatch(
                        componentsActionCreators.listComponents(
                            'public',
                            '',
                            { idPattern: mediaFilters },
                            sort,
                            take,
                            0
                        )
                    );
                    setUrlState('');
                }
                break;
            case 'slidedecks':
                if (_urlState.length > 0) {
                    setUrlState(_urlState);
                    dispatch(
                        slidedecksActionCreators.listSlidedecks(
                            'public',
                            _urlState,
                            { idPattern: mediaFilters },
                            sort,
                            take,
                            0
                        )
                    );
                } else {
                    dispatch(
                        slidedecksActionCreators.listSlidedecks(
                            'public',
                            '',
                            { idPattern: mediaFilters },
                            sort,
                            take,
                            0
                        )
                    );
                    setUrlState('');
                }
                break;
            case 'media':
                if (_urlState.length > 0) {
                    setUrlState(_urlState);
                    dispatch(
                        mediaActionCreators.listMedias(
                            _urlState,
                            { idPattern: mediaFilters || [] },
                            sort,
                            take,
                            0
                        )
                    );
                } else {
                    dispatch(
                        mediaActionCreators.listMedias(
                            '',
                            { idPattern: mediaFilters || [] },
                            sort,
                            take,
                            0
                        )
                    );
                    setUrlState('');
                }
                break;
            case 'contentList':
                if (_urlState.length > 0) {
                    setUrlState(_urlState);
                    dispatch(
                        contentListActionCreators.listContentLists(
                            _urlState,
                            { idPattern: mediaFilters },
                            sort,
                            take,
                            0
                        )
                    );
                } else {
                    dispatch(
                        contentListActionCreators.listContentLists(
                            '',
                            { idPattern: mediaFilters },
                            sort,
                            take,
                            0
                        )
                    );
                    setUrlState('');
                }
        }
    };

    const onClickBreadCrumb = (crumb: any, index: string) => {
        onClickFolder(crumb, false, parseInt(index));
    };

    const i18n = commonHooks.useLocalization();

    useEffect(() => {
        if (!nameValueRef?.current) {
            const delayDebounceFn = setTimeout(() => {
                const idPattern = nameFilter ? [`*${nameFilter}*`] : [];
                // send request for search
                setSkip(0);
                switch (tableType) {
                    case 'pages':
                        if (urlState.length > 0) {
                            dispatch(
                                pagesActionCreators.loadPages(
                                    urlState,
                                    { idPattern: idPattern },
                                    sort,
                                    take,
                                    0
                                )
                            );
                        } else {
                            dispatch(
                                pagesActionCreators.loadPages(
                                    '',
                                    { idPattern: idPattern },
                                    sort,
                                    take,
                                    0
                                )
                            );
                        }
                        break;
                    case 'themes':
                        if (urlState.length > 0) {
                            dispatch(
                                themesActionCreators.listThemes(
                                    urlState,
                                    { idPattern: idPattern },
                                    sort,
                                    take,
                                    0
                                )
                            );
                        } else {
                            dispatch(
                                themesActionCreators.listThemes(
                                    '',
                                    { idPattern: idPattern },
                                    sort,
                                    take,
                                    0
                                )
                            );
                        }
                        break;
                    case 'components':
                        if (urlState.length > 0) {
                            dispatch(
                                componentsActionCreators.listComponents(
                                    'public',
                                    urlState,
                                    {
                                        idPattern: idPattern,
                                    },
                                    sort,
                                    take,
                                    0
                                )
                            );
                        } else {
                            dispatch(
                                componentsActionCreators.listComponents(
                                    'public',
                                    '',
                                    { idPattern: idPattern },
                                    sort,
                                    take,
                                    0
                                )
                            );
                        }
                        break;
                    case 'slidedecks':
                        if (urlState.length > 0) {
                            dispatch(
                                slidedecksActionCreators.listSlidedecks(
                                    'public',
                                    urlState,
                                    {
                                        idPattern: idPattern,
                                    },
                                    sort,
                                    take,
                                    0
                                )
                            );
                        } else {
                            dispatch(
                                slidedecksActionCreators.listSlidedecks(
                                    'public',
                                    '',
                                    { idPattern: idPattern },
                                    sort,
                                    take,
                                    0
                                )
                            );
                        }
                        break;
                    case 'media':
                        if (urlState.length > 0) {
                            dispatch(
                                mediaActionCreators.listMedias(
                                    urlState,
                                    { idPattern: idPattern },
                                    sort,
                                    take,
                                    0
                                )
                            );
                        } else {
                            dispatch(
                                mediaActionCreators.listMedias(
                                    '',
                                    { idPattern: idPattern },
                                    sort,
                                    take,
                                    0
                                )
                            );
                        }
                }
            }, 1000);

            return () => clearTimeout(delayDebounceFn);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nameFilter]);

    const handleSelectUser = (selectedUsers: any) => {
        setSkip(0);
        const delayDebounceFn = setTimeout(() => {
            const authorsIn = selectedUsers;
            setAuthorsInArray(authorsIn);
            // send request for search
            switch (tableType) {
                case 'pages':
                    if (urlState.length > 0) {
                        dispatch(
                            pagesActionCreators.loadPages(urlState, { authorsIn }, sort, take, 0)
                        );
                    } else {
                        dispatch(pagesActionCreators.loadPages('', { authorsIn }, sort, take, 0));
                    }
                    break;
                case 'themes':
                    if (urlState.length > 0) {
                        dispatch(
                            themesActionCreators.listThemes(urlState, { authorsIn }, sort, take, 0)
                        );
                    } else {
                        dispatch(themesActionCreators.listThemes('', { authorsIn }, sort, take, 0));
                    }
                    break;
                case 'components':
                    if (urlState.length > 0) {
                        dispatch(
                            componentsActionCreators.listComponents(
                                'public',
                                urlState,
                                {
                                    authorsIn,
                                },
                                sort,
                                take,
                                0
                            )
                        );
                    } else {
                        dispatch(
                            componentsActionCreators.listComponents(
                                'public',
                                '',
                                { authorsIn },
                                sort,
                                take,
                                0
                            )
                        );
                    }
                    break;
                case 'slidedecks':
                    if (urlState.length > 0) {
                        dispatch(
                            slidedecksActionCreators.listSlidedecks(
                                'public',
                                urlState,
                                {
                                    authorsIn,
                                },
                                sort,
                                take,
                                0
                            )
                        );
                    } else {
                        dispatch(
                            slidedecksActionCreators.listSlidedecks(
                                'public',
                                '',
                                { authorsIn },
                                sort,
                                take,
                                0
                            )
                        );
                    }
                    break;
                case 'media':
                    if (urlState.length > 0) {
                        dispatch(
                            mediaActionCreators.listMedias(
                                urlState,
                                { idPattern: [], authorsIn },
                                sort,
                                take,
                                0
                            )
                        );
                    } else {
                        dispatch(
                            mediaActionCreators.listMedias(
                                '',
                                { idPattern: [], authorsIn },
                                sort,
                                take,
                                0
                            )
                        );
                    }
            }
        }, 1000);

        return () => clearTimeout(delayDebounceFn);
    };

    useEffect(() => {
        // const idPattern = `*${nameFilter}*`;
        const idPattern = nameFilter ? [`*${nameFilter}*`] : [];
        const authorsIn = authorsInArray;
        if (Object.values(sort).some((x) => x)) {
            setSkip(0);
            switch (tableType) {
                case 'pages':
                    if (urlState.length > 0) {
                        dispatch(
                            pagesActionCreators.loadPages(
                                urlState,
                                { idPattern: idPattern, authorsIn },
                                sort,
                                take,
                                0
                            )
                        );
                    } else {
                        dispatch(
                            pagesActionCreators.loadPages(
                                '',
                                { idPattern: idPattern, authorsIn },
                                sort,
                                take,
                                0
                            )
                        );
                    }
                    break;
                case 'themes':
                    if (urlState.length > 0) {
                        dispatch(
                            themesActionCreators.listThemes(
                                urlState,
                                { idPattern: idPattern, authorsIn },
                                sort,
                                take,
                                0
                            )
                        );
                    } else {
                        dispatch(
                            themesActionCreators.listThemes(
                                '',
                                { idPattern: idPattern, authorsIn },
                                sort,
                                take,
                                0
                            )
                        );
                    }
                    break;
                case 'components':
                    if (urlState.length > 0) {
                        dispatch(
                            componentsActionCreators.listComponents(
                                'public',
                                urlState,
                                {
                                    idPattern: idPattern,
                                    authorsIn,
                                },
                                sort,
                                take,
                                0
                            )
                        );
                    } else {
                        dispatch(
                            componentsActionCreators.listComponents(
                                'public',
                                '',
                                { idPattern: idPattern, authorsIn },
                                sort,
                                take,
                                0
                            )
                        );
                    }
                    break;
                case 'slidedecks':
                    if (urlState.length > 0) {
                        dispatch(
                            slidedecksActionCreators.listSlidedecks(
                                'public',
                                urlState,
                                {
                                    idPattern: idPattern,
                                    authorsIn,
                                },
                                sort,
                                take,
                                0
                            )
                        );
                    } else {
                        dispatch(
                            slidedecksActionCreators.listSlidedecks(
                                'public',
                                '',
                                { idPattern: idPattern, authorsIn },
                                sort,
                                take,
                                0
                            )
                        );
                    }
                    break;
                case 'media':
                    if (urlState.length > 0) {
                        dispatch(
                            mediaActionCreators.listMedias(
                                urlState,
                                { idPattern: idPattern, authorsIn },
                                sort,
                                take,
                                0
                            )
                        );
                    } else {
                        dispatch(
                            mediaActionCreators.listMedias(
                                '',
                                { idPattern: idPattern, authorsIn },
                                sort,
                                take,
                                0
                            )
                        );
                    }
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort]);

    const tableTypeForPagination = (takeparams: number, skipParams: number) => {
        const idPattern = nameFilter ? [`*${nameFilter}*`] : [];
        const authorsIn = authorsInArray;
        switch (tableType) {
            case 'pages':
                if (urlState.length > 0) {
                    dispatch(
                        pagesActionCreators.loadPages(
                            urlState,
                            { idPattern: idPattern, authorsIn },
                            sort,
                            takeparams,
                            skipParams
                        )
                    );
                } else {
                    dispatch(
                        pagesActionCreators.loadPages(
                            '',
                            { idPattern: idPattern, authorsIn },
                            sort,
                            takeparams,
                            skipParams
                        )
                    );
                }
                break;
            case 'themes':
                if (urlState.length > 0) {
                    dispatch(
                        themesActionCreators.listThemes(
                            urlState,
                            { idPattern: idPattern, authorsIn },
                            sort,
                            takeparams,
                            skipParams
                        )
                    );
                } else {
                    dispatch(
                        themesActionCreators.listThemes(
                            '',
                            { idPattern: idPattern, authorsIn },
                            sort,
                            takeparams,
                            skipParams
                        )
                    );
                }
                break;
            case 'components':
                if (urlState.length > 0) {
                    dispatch(
                        componentsActionCreators.listComponents(
                            'public',
                            urlState,
                            {
                                idPattern: idPattern,
                                authorsIn,
                            },
                            sort,
                            takeparams,
                            skipParams
                        )
                    );
                } else {
                    dispatch(
                        componentsActionCreators.listComponents(
                            'public',
                            '',
                            { idPattern: idPattern, authorsIn },
                            sort,
                            takeparams,
                            skipParams
                        )
                    );
                }
                break;
            case 'slidedecks':
                if (urlState.length > 0) {
                    dispatch(
                        slidedecksActionCreators.listSlidedecks(
                            'public',
                            urlState,
                            {
                                idPattern: idPattern,
                                authorsIn,
                            },
                            sort,
                            takeparams,
                            skipParams
                        )
                    );
                } else {
                    dispatch(
                        slidedecksActionCreators.listSlidedecks(
                            'public',
                            '',
                            { idPattern: idPattern, authorsIn },
                            sort,
                            takeparams,
                            skipParams
                        )
                    );
                }
                break;
            case 'media':
                if (urlState.length > 0) {
                    dispatch(
                        mediaActionCreators.listMedias(
                            urlState,
                            { idPattern: idPattern, authorsIn },
                            sort,
                            takeparams,
                            skipParams
                        )
                    );
                } else {
                    dispatch(
                        mediaActionCreators.listMedias(
                            '',
                            { idPattern: idPattern, authorsIn },
                            sort,
                            takeparams,
                            skipParams
                        )
                    );
                }
        }
    };

    const loadNextPage = () => {
        setSkip(skip + take);
        tableTypeForPagination(take, skip + take);
    };

    const loadPrevPage = () => {
        setSkip(Math.max(0, skip - take));
        tableTypeForPagination(take, skip - take);
    };

    const pageSizeChanged = (e: any) => {
        setTake(Number(e.target.value));
        setSkip(0);
        tableTypeForPagination(Number(e.target.value), 0);
    };

    const getReosurceId = (id: string) => {
        if (id?.includes('/production/pages')) {
            return `/screenshots/${id?.substring(id?.indexOf('/pages'))}`;
        } else if (id?.includes('public/components')) {
            return `/screenshots/components/${id}`;
        } else if (id?.includes('public/slidedecks')) {
            return `/screenshots/slidedecks/${id}`;
        } else if (tableType === 'media') {
            // For media files, extract the path after /assets/
            if (id?.includes('/assets/')) {
                return id.substring(id.indexOf('/assets/') + 1); // Keep the assets/ prefix
            }
            return id;
        } else if (id?.includes('/assets/images/')) {
            return id.substring(id.indexOf('/assets/') + 1); // Keep the assets/ prefix
        } else if (id?.includes('/assets/fonts/')) {
            return id.substring(id.indexOf('/assets/') + 1); // Keep the assets/ prefix
        } else {
            return `assets/${id}`;
        }
    };

    return (
        <div className={`items-table ${selectedItems.length === 0 ? 'expanded' : ''}`}>
            <div className="table-controls-container">
                <div>
                    <div className="table-title-container">
                        {rest.title && <h3 className="title">{rest.title}</h3>}
                        {crumbs.length > 1 && (
                            <BreadCrumb
                                crumbs={crumbs}
                                onClickBreadCrumb={onClickBreadCrumb}
                                disableParentPath={
                                    mediaBasePath
                                        ? ['fonts', 'images'].includes(mediaBasePath)
                                        : false
                                }
                            />
                        )}
                    </div>
                    {rest.subtitle && <h4 className="sub-title">{rest.subtitle}</h4>}
                </div>

                <div className="table-controls">
                    {rest.options && rest.options.length > 0 && (
                        <div className="table-options">
                            {rest.options.map((opt: any) => (
                                <div onClick={() => rest.onClickOption && rest.onClickOption(opt)}>
                                    <img
                                        src={
                                            opt?.name === rest.selectedOption?.name
                                                ? opt.activeIcon
                                                : opt.icon
                                        }
                                        alt={opt.name}
                                        className={'main-left-sidebar-icon icon'}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="show-deleted-toggle">
                        <CheckboxSwitch
                            title="Show deleted"
                            checked={showDeletedItems || false}
                            onChange={() => onClickShowDeletedItems && onClickShowDeletedItems()}
                        />
                    </div>

                    <div>
                        <Button
                            className="prev-button"
                            size="sm"
                            variant="outline"
                            disabled={skip <= 0}
                            onClick={loadPrevPage}
                        >
                            <img className="prev" src={chevronRightSvg} alt="load prev page" />
                        </Button>
                        <select value={take || 5} onChange={pageSizeChanged}>
                            {Object.keys(pageSizes).map((k: string) => (
                                <option key={k} value={pageSizes[k]}>
                                    {k.slice(1)}
                                </option>
                            ))}
                        </select>
                        <Button
                            size="sm"
                            disabled={!!(items && items.length < take)}
                            onClick={loadNextPage}
                            className="next-button"
                        >
                            <img className="next" src={chevronRightSvg} alt="load next page" />
                        </Button>
                    </div>

                    <div className="grid-list-toggle-container">
                        <div
                            className={`grid-list-toggle grid-toggle ${
                                showGridView ? 'selected' : ''
                            }`}
                            onClick={() => setShowGridView(true)}
                        >
                            <img
                                src={showGridView ? gridIconWhite : gridIconGrey}
                                alt="show grid view"
                            />
                        </div>
                        <div
                            className={`grid-list-toggle list-toggle ${
                                showGridView ? '' : 'selected'
                            }`}
                            onClick={() => setShowGridView(false)}
                        >
                            <img
                                src={showGridView ? listIconGrey : listIconWhite}
                                alt="show list view"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className={`grid-view ${showGridView ? '' : 'hidden'}`}>
                {items &&
                    items.length > 0 &&
                    items.map((r: any, _i: number) => (
                        <div
                            className={`grid-item ${
                                selectedItems.includes(r.name) ? 'selected' : ''
                            }`}
                            key={r.name}
                            onClick={() => {
                                // For folders, navigate directly without selecting
                                if (r.isImplicit === true || r.headers['ldr-type'] !== undefined) {
                                    onClickFolder(r.name, true);
                                } else if (selectedItems.includes(r.name)) {
                                    onClickItem(r.name);
                                } else {
                                    onClickRow(r.name, selectedItems.includes(r.name));
                                }
                            }}
                        >
                            <Thumbnail
                                resourceId={getReosurceId(r?.id)}
                                size={'1920x1080'}
                                isFolder={r.headers['ldr-type'] !== undefined}
                            />
                            <div className="name">{r.name}</div>
                        </div>
                    ))}
            </div>

            <Table className={`table-view ${showGridView ? 'hidden' : ''}`}>
                <thead>
                    <tr className="row">
                        <th></th>
                        <th className="name-header">
                            {showNameFilter ? (
                                <input
                                    id="name-filter-input"
                                    type="text"
                                    value={nameFilter}
                                    onChange={(e) => {
                                        if (nameValueRef?.current) {
                                            nameValueRef.current = false;
                                        }
                                        setNameFilter(e.target.value);
                                    }}
                                    placeholder={`Filter By ${i18n.t(`${tableType}_table_name`)}`}
                                    autoFocus
                                />
                            ) : (
                                i18n.t(`${tableType}_table_name`)
                            )}
                            <div className={'header-buttons'}>
                                <button
                                    className={'sort-button'}
                                    onClick={() => {
                                        setSort({
                                            field: 'Id',
                                            dir: sort.dir === 'asc' ? 'desc' : 'asc',
                                        });
                                    }}
                                >
                                    {sort.field === 'Id' ? (
                                        <img
                                            className={'sort-img-one'}
                                            src={
                                                sort.dir === 'desc'
                                                    ? chevronDownSvgTwo
                                                    : chevronUpSvg
                                            }
                                            alt={`Sort By ${i18n.t(`${tableType}_table_name`)}`}
                                        />
                                    ) : (
                                        <>
                                            <img
                                                className={'sort-img'}
                                                src={chevronUpSvg}
                                                alt={`Sort By ${i18n.t(`${tableType}_table_name`)}`}
                                            />
                                            <img
                                                className={'sort-img'}
                                                src={chevronDownSvgTwo}
                                                alt={`Sort By ${i18n.t(`${tableType}_table_name`)}`}
                                            />
                                        </>
                                    )}
                                </button>
                                <img
                                    className={'filter-img'}
                                    src={showNameFilter ? filterIconActiveSvg : filterIconSvg}
                                    alt={`Filter ${i18n.t(`${tableType}_table_name`)}`}
                                    onClick={() => {
                                        setShowNameFilter(!showNameFilter);
                                        setNameFilter('');
                                    }}
                                />
                            </div>
                        </th>
                        <th>{i18n.t(`${tableType}_table_tags`)}</th>
                        <th>{i18n.t(`${tableType}_table_version`)}</th>
                        <th>
                            <UserFilterInput
                                display={showAuthorFilter}
                                onSelectUser={handleSelectUser}
                                closeDropdown={() => setShowAuthorFilter(false)}
                                toggleIconRef={authorFilterIconRef}
                            />
                            <div style={{ display: !showAuthorFilter ? 'inline-block' : 'none' }}>
                                {i18n.t(`${tableType}_table_created_by`)}
                            </div>
                            <div className={'header-buttons'}>
                                <button
                                    className={'sort-button'}
                                    onClick={() =>
                                        setSort({
                                            field: 'Author',
                                            dir: sort.dir === 'asc' ? 'desc' : 'asc',
                                        })
                                    }
                                >
                                    {sort.field === 'Author' ? (
                                        <img
                                            className={'sort-img-one'}
                                            src={
                                                sort.dir === 'desc'
                                                    ? chevronDownSvgTwo
                                                    : chevronUpSvg
                                            }
                                            alt={`Sort By ${i18n.t(`${tableType}_table_name`)}`}
                                        />
                                    ) : (
                                        <>
                                            <img
                                                className={'sort-img'}
                                                src={chevronUpSvg}
                                                alt={`Sort By ${i18n.t(`${tableType}_table_name`)}`}
                                            />
                                            <img
                                                className={'sort-img'}
                                                src={chevronDownSvgTwo}
                                                alt={`Sort By ${i18n.t(`${tableType}_table_name`)}`}
                                            />
                                        </>
                                    )}
                                </button>
                                <img
                                    ref={authorFilterIconRef}
                                    id={'user-filter-img'}
                                    className={'filter-img'}
                                    src={showAuthorFilter ? filterIconActiveSvg : filterIconSvg}
                                    alt={`Filter ${i18n.t(`${tableType}_table_created_by`)}`}
                                    onClick={() => setShowAuthorFilter(!showAuthorFilter)}
                                />
                            </div>
                        </th>
                        <th>
                            {i18n.t(`${tableType}_table_modified_date`)}
                            <button
                                className={'sort-button'}
                                onClick={() =>
                                    setSort({
                                        field: 'LastModifiedDate',
                                        dir: sort.dir === 'asc' ? 'desc' : 'asc',
                                    })
                                }
                            >
                                {sort.field === 'LastModifiedDate' ? (
                                    <img
                                        className={'sort-img-one'}
                                        src={sort.dir === 'desc' ? chevronDownSvgTwo : chevronUpSvg}
                                        alt={`Sort By ${i18n.t(`${tableType}_table_name`)}`}
                                    />
                                ) : (
                                    <>
                                        <img
                                            className={'sort-img'}
                                            src={chevronUpSvg}
                                            alt={`Sort By ${i18n.t(`${tableType}_table_name`)}`}
                                        />
                                        <img
                                            className={'sort-img'}
                                            src={chevronDownSvgTwo}
                                            alt={`Sort By ${i18n.t(`${tableType}_table_name`)}`}
                                        />
                                    </>
                                )}
                            </button>
                        </th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {items &&
                        items.length > 0 &&
                        items.map((r: any, i: number) => (
                            <tr
                                className={`row ${
                                    selectedItems.includes(r.name) ? 'selected' : ''
                                } ${r.isDeleted && 'deleted-item'}`}
                                data-page-name={r.name}
                                onClick={() => {
                                    // For folders, navigate directly without selecting
                                    if (
                                        r.isImplicit === true ||
                                        r.headers['ldr-type'] !== undefined
                                    ) {
                                        onClickFolder(r.name, true);
                                    } else {
                                        onClickRow(r.name, selectedItems.includes(r.name));
                                    }
                                }}
                                key={r.name}
                            >
                                <td>
                                    <Thumbnail
                                        resourceId={getReosurceId(r?.id)}
                                        size={'1920x1080'}
                                        isFolder={r.headers['ldr-type'] !== undefined}
                                    />
                                </td>
                                <td className="name-cell">
                                    <button
                                        key={i}
                                        onClick={
                                            r.isImplicit === true ||
                                            r.headers['ldr-type'] !== undefined
                                                ? () => onClickFolder(r.name, true)
                                                : () => onClickItem(r.name)
                                        }
                                    >
                                        {r.name}
                                    </button>
                                </td>
                                <td data-testid="tags-table-cell">
                                    {r.tags && r.tags.length > 0 ? r.tags.join(', ') : '-'}
                                </td>
                                <td>{r.versionNumber}</td>
                                <td>
                                    <AuthorEmail authorId={r.author} />
                                </td>
                                <td data-testid="modified-date-table-cell">
                                    {new Date(r.lastModified).toLocaleDateString()}
                                </td>
                                <td>
                                    <img src={lockSvg} alt="File icon" />
                                </td>
                            </tr>
                        ))}
                </tbody>
            </Table>
        </div>
    );
};

export default ItemsTable;
